import React, { useMemo, useState } from 'react';
import { formatMoney } from '@/lib/money';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Ticket,
  Search,
  ArrowLeft,
  Plus,
  Copy,
  Trash2,
  Edit,
  Loader2,
  Shuffle,
  Download,
  MoreHorizontal,
  Percent,
  DollarSign,
  Truck,
  BarChart3,
  Power,
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  MultiSelectPicker,
  type PickerAsyncSource,
  type PickerItem,
} from '@/components/ui/multi-select-picker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ── Types ──

type CouponType = 'percentage' | 'fixed_amount' | 'free_shipping';
type CouponStatus = 'active' | 'expired' | 'inactive';

interface Coupon {
  _id: string;
  code: string;
  type: CouponType;
  value: number;
  currency?: string;
  minOrderAmount?: number;
  maxDiscountAmount?: number;
  maxRedemptions: number;
  perCustomerLimit: number;
  redemptionCount: number;
  firstOrderOnly: boolean;
  validFrom?: string;
  validUntil?: string;
  applicableProductIds?: string[];
  applicableCategoryIds?: string[];
  excludedProductIds?: string[];
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CouponsMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface CouponFormData {
  code: string;
  type: CouponType;
  value: number;
  currency: string;
  minOrderAmount: number | '';
  maxDiscountAmount: number | '';
  maxRedemptions: number;
  perCustomerLimit: number;
  firstOrderOnly: boolean;
  validFrom: string;
  validUntil: string;
  /**
   * Targeting fields — backed by the MultiSelectPicker. Stored as arrays of
   * IDs (was comma-separated strings; migrated in 2026-05 because the
   * "type IDs by hand" UX was bug-prone and nobody can remember an ObjectId).
   */
  applicableProductIds: string[];
  applicableCategoryIds: string[];
  excludedProductIds: string[];
  description: string;
}

interface CouponReport {
  totalCoupons: number;
  activeCoupons: number;
  totalRedemptions: number;
  totalDiscountGiven: number;
  currency: string;
}

// ── Helpers ──

const COUPON_TYPES: { value: CouponType; label: string; icon: React.ReactNode }[] = [
  { value: 'percentage', label: 'Percentage', icon: <Percent className="h-4 w-4" /> },
  { value: 'fixed_amount', label: 'Fixed Amount', icon: <DollarSign className="h-4 w-4" /> },
  { value: 'free_shipping', label: 'Free Shipping', icon: <Truck className="h-4 w-4" /> },
];

function getCouponStatus(coupon: Coupon): CouponStatus {
  if (!coupon.isActive) return 'inactive';
  if (coupon.validUntil && new Date(coupon.validUntil) < new Date()) return 'expired';
  if (coupon.maxRedemptions > 0 && coupon.redemptionCount >= coupon.maxRedemptions) {
    return 'expired';
  }
  return 'active';
}

function isExpiringSoon(coupon: Coupon): boolean {
  if (!coupon.validUntil) return false;
  const until = new Date(coupon.validUntil);
  const now = new Date();
  const daysLeft = (until.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return daysLeft > 0 && daysLeft <= 7;
}

function getStatusBadge(coupon: Coupon) {
  const status = getCouponStatus(coupon);
  const expiringSoon = isExpiringSoon(coupon);

  if (status === 'active' && expiringSoon) {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
        Expiring Soon
      </Badge>
    );
  }

  const map: Record<CouponStatus, { label: string; className: string }> = {
    active: {
      label: 'Active',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    expired: {
      label: 'Expired',
      className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
    inactive: {
      label: 'Inactive',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    },
  };
  const entry = map[status];
  return <Badge className={entry.className}>{entry.label}</Badge>;
}

function formatCouponValue(coupon: Coupon): string {
  if (coupon.type === 'percentage') return `${coupon.value}%`;
  if (coupon.type === 'free_shipping') return 'Free Shipping';
  // coupon.value is minor units — delegate to the single money formatter.
  return formatMoney(coupon.value, coupon.currency || 'INR', { alwaysDecimals: true });
}

/**
 * Map a `/products/picker` row into the PickerItem shape the picker renders.
 * The `secondary` line is built so a search across name+secondary finds the
 * product by either the product SKU or any variant SKU.
 */
function rowToPickerItem(row: {
  _id: string;
  name: string;
  sku?: string;
  variantSkus?: string[];
  variantCount?: number;
}): PickerItem {
  const parts: string[] = [];
  if (row.sku) parts.push(`SKU: ${row.sku}`);
  const vskus = row.variantSkus ?? [];
  const vcount = row.variantCount ?? vskus.length;
  if (vskus.length > 0) {
    parts.push(
      `${vcount} variant${vcount === 1 ? '' : 's'} (${vskus.join(', ')}${
        vcount > vskus.length ? '…' : ''
      })`
    );
  }
  return {
    value: row._id,
    label: row.name,
    secondary: parts.join(' · ') || undefined,
  };
}

function generateRandomCode(length = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const emptyFormData: CouponFormData = {
  code: '',
  type: 'percentage',
  value: 0,
  currency: 'INR',
  minOrderAmount: '',
  maxDiscountAmount: '',
  maxRedemptions: 0,
  perCustomerLimit: 0,
  firstOrderOnly: false,
  validFrom: '',
  validUntil: '',
  applicableProductIds: [],
  applicableCategoryIds: [],
  excludedProductIds: [],
  description: '',
};

// ── Component ──

export default function Coupons() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  // List state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState<CouponFormData>(emptyFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Bulk generate state
  const [showBulkGenerate, setShowBulkGenerate] = useState(false);
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkPrefix, setBulkPrefix] = useState('');
  const [bulkFormData, setBulkFormData] = useState<CouponFormData>(emptyFormData);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);

  // ── Queries ──

  const {
    data: couponsResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['coupons', projectId, page, limit, statusFilter, typeFilter, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (search) params.search = search;
      const response = await api.get(`/projects/${projectId}/ecommerce/coupons`, { params });
      return response.data as { success: boolean; data: Coupon[]; meta: CouponsMeta };
    },
    enabled: Boolean(projectId),
  });

  const { data: reportResponse } = useQuery({
    queryKey: ['coupons-report', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/coupons/report`);
      return response.data as { success: boolean; data: CouponReport };
    },
    enabled: Boolean(projectId),
  });

  const { data: storeSettingsResponse } = useQuery({
    queryKey: ['store-settings', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/settings`);
      return response.data as { success: boolean; data: { currency?: string } };
    },
    enabled: Boolean(projectId),
  });

  const { data: pickerCategoriesResponse, isLoading: loadingCategories } = useQuery({
    queryKey: ['coupon-picker-categories', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/ecommerce/categories`);
      return response.data as {
        success: boolean;
        data: Array<{ _id: string; name: string; productCount?: number; slug?: string }>;
      };
    },
    enabled: Boolean(projectId),
  });

  /**
   * Async product picker source.
   *
   * Hits the dedicated lite endpoint `/products/picker`, which returns a tiny
   * `{ _id, name, sku, variantSkus, variantCount }` projection — no full
   * product docs, no `countDocuments`, no skip/limit. The endpoint supports
   * three modes the picker calls on demand:
   *
   *   - `?ids=…` for chip hydration in edit mode (called once on mount with
   *     the existing coupon's applicableProductIds / excludedProductIds).
   *   - `?q=…` for server-side text search (debounced inside the picker).
   *   - `?cursor=…` for infinite-scroll catalog pagination.
   *
   * Cost-wise this scales to 100k+ products: hydration is one indexed
   * `_id $in` lookup, search uses the existing text index, scroll uses
   * cursor pagination. For stores under 200 products the user gets the
   * whole catalog in one round-trip and never sees pagination.
   */
  const productPickerSource = useMemo(
    (): PickerAsyncSource => ({
      fetchPage: async ({ query: q, cursor }) => {
        const params: Record<string, string | number> = { limit: 50 };
        if (q) params.q = q;
        if (cursor) params.cursor = cursor;
        const response = await api.get(`/projects/${projectId}/ecommerce/products/picker`, {
          params,
        });
        const body = response.data as {
          success: boolean;
          data: Array<{
            _id: string;
            name: string;
            sku?: string;
            variantSkus?: string[];
            variantCount?: number;
          }>;
          nextCursor: string | null;
        };
        return {
          items: (body.data ?? []).map(rowToPickerItem),
          nextCursor: body.nextCursor ?? null,
        };
      },
      hydrate: async (ids) => {
        if (ids.length === 0) return [];
        const response = await api.get(`/projects/${projectId}/ecommerce/products/picker`, {
          params: { ids: ids.join(',') },
        });
        const body = response.data as {
          success: boolean;
          data: Array<{
            _id: string;
            name: string;
            sku?: string;
            variantSkus?: string[];
            variantCount?: number;
          }>;
        };
        return (body.data ?? []).map(rowToPickerItem);
      },
    }),
    [projectId]
  );

  const categoryPickerItems: PickerItem[] = useMemo(() => {
    const rows = pickerCategoriesResponse?.data ?? [];
    return rows.map((c) => ({
      value: c._id,
      label: c.name,
      secondary:
        typeof c.productCount === 'number'
          ? `${c.productCount} product${c.productCount === 1 ? '' : 's'}`
          : c.slug,
    }));
  }, [pickerCategoriesResponse]);

  const coupons = couponsResponse?.data || [];
  const meta = couponsResponse?.meta || { page: 1, limit: 20, total: 0, totalPages: 0 };
  const report = reportResponse?.data;
  const storeCurrency = storeSettingsResponse?.data?.currency || 'INR';

  // ── Mutations ──

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const response = await api.post(`/projects/${projectId}/ecommerce/coupons`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', projectId] });
      queryClient.invalidateQueries({ queryKey: ['coupons-report', projectId] });
      toast.success('Coupon created');
      closeForm();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to create coupon');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ couponId, data }: { couponId: string; data: Record<string, any> }) => {
      const response = await api.put(`/projects/${projectId}/ecommerce/coupons/${couponId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', projectId] });
      queryClient.invalidateQueries({ queryKey: ['coupons-report', projectId] });
      toast.success('Coupon updated');
      closeForm();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to update coupon');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (couponId: string) => {
      const response = await api.delete(`/projects/${projectId}/ecommerce/coupons/${couponId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', projectId] });
      queryClient.invalidateQueries({ queryKey: ['coupons-report', projectId] });
      toast.success('Coupon deactivated');
      setShowDeleteConfirm(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to deactivate coupon');
    },
  });

  const cloneMutation = useMutation({
    mutationFn: async (couponId: string) => {
      const response = await api.post(`/projects/${projectId}/ecommerce/coupons/${couponId}/clone`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons', projectId] });
      queryClient.invalidateQueries({ queryKey: ['coupons-report', projectId] });
      toast.success('Coupon duplicated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to duplicate coupon');
    },
  });

  const bulkGenerateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const response = await api.post(
        `/projects/${projectId}/ecommerce/coupons/bulk-generate`,
        data
      );
      return response.data;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['coupons', projectId] });
      queryClient.invalidateQueries({ queryKey: ['coupons-report', projectId] });
      const codes = res?.data?.codes || res?.data?.map?.((c: any) => c.code) || [];
      setGeneratedCodes(codes);
      toast.success(`${codes.length} coupons generated`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to generate coupons');
    },
  });

  // ── Form helpers ──

  function openCreateForm() {
    setEditingCoupon(null);
    setFormData({ ...emptyFormData, currency: storeCurrency });
    setShowForm(true);
  }

  function openEditForm(coupon: Coupon) {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      type: coupon.type,
      value: coupon.type === 'fixed_amount' ? coupon.value / 100 : coupon.value,
      currency: coupon.currency || storeCurrency,
      minOrderAmount: coupon.minOrderAmount ? coupon.minOrderAmount / 100 : '',
      maxDiscountAmount: coupon.maxDiscountAmount ? coupon.maxDiscountAmount / 100 : '',
      maxRedemptions: coupon.maxRedemptions,
      perCustomerLimit: coupon.perCustomerLimit,
      firstOrderOnly: coupon.firstOrderOnly,
      validFrom: coupon.validFrom ? coupon.validFrom.slice(0, 16) : '',
      validUntil: coupon.validUntil ? coupon.validUntil.slice(0, 16) : '',
      applicableProductIds: coupon.applicableProductIds ?? [],
      applicableCategoryIds: coupon.applicableCategoryIds ?? [],
      excludedProductIds: coupon.excludedProductIds ?? [],
      description: coupon.description || '',
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingCoupon(null);
    setFormData(emptyFormData);
  }

  function buildPayload(data: CouponFormData): Record<string, any> {
    const payload: Record<string, any> = {
      code: data.code.toUpperCase().trim(),
      type: data.type,
      value:
        data.type === 'fixed_amount' ? Math.round(Number(data.value) * 100) : Number(data.value),
      maxRedemptions: Number(data.maxRedemptions),
      perCustomerLimit: Number(data.perCustomerLimit),
      firstOrderOnly: data.firstOrderOnly,
    };
    if (data.type === 'fixed_amount') payload.currency = data.currency;
    if (data.minOrderAmount !== '' && Number(data.minOrderAmount) > 0) {
      payload.minOrderAmount = Math.round(Number(data.minOrderAmount) * 100);
    }
    if (
      data.type === 'percentage' &&
      data.maxDiscountAmount !== '' &&
      Number(data.maxDiscountAmount) > 0
    ) {
      payload.maxDiscountAmount = Math.round(Number(data.maxDiscountAmount) * 100);
    }
    if (data.validFrom) payload.validFrom = new Date(data.validFrom).toISOString();
    if (data.validUntil) payload.validUntil = new Date(data.validUntil).toISOString();
    if (data.description.trim()) payload.description = data.description.trim();

    // Targeting fields are already arrays (driven by the multi-select pickers).
    // Only include them in the payload when non-empty so the API treats an
    // empty selection as "applies to everything" rather than "applies to nothing".
    if (data.applicableProductIds.length > 0) {
      payload.applicableProductIds = data.applicableProductIds;
    }
    if (data.applicableCategoryIds.length > 0) {
      payload.applicableCategoryIds = data.applicableCategoryIds;
    }
    if (data.excludedProductIds.length > 0) {
      payload.excludedProductIds = data.excludedProductIds;
    }

    return payload;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = buildPayload(formData);
    if (editingCoupon) {
      updateMutation.mutate({ couponId: editingCoupon._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleBulkSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = buildPayload(bulkFormData);
    delete payload.code;
    (payload as any).count = bulkCount;
    if (bulkPrefix.trim()) (payload as any).prefix = bulkPrefix.trim().toUpperCase();
    bulkGenerateMutation.mutate(payload);
  }

  function downloadGeneratedCodes() {
    const blob = new Blob([generatedCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'generated-coupons.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Search / Filter handlers ──

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // ── Render helpers ──

  function renderCouponForm(
    data: CouponFormData,
    setData: React.Dispatch<React.SetStateAction<CouponFormData>>
  ) {
    return (
      <div className="grid gap-4">
        {/* Code */}
        <div className="grid gap-2">
          <Label htmlFor="code">Coupon Code</Label>
          <div className="flex gap-2">
            <Input
              id="code"
              value={data.code}
              onChange={(e) => setData((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
              placeholder="e.g. SUMMER2024"
              className="font-mono uppercase"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setData((d) => ({ ...d, code: generateRandomCode() }))}
              title="Generate Random Code"
            >
              <Shuffle className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Type + Value */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>Discount Type</Label>
            <Select
              value={data.type}
              onValueChange={(v) => setData((d) => ({ ...d, type: v as CouponType }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUPON_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {data.type !== 'free_shipping' && (
            <div className="grid gap-2">
              <Label htmlFor="value">
                {data.type === 'percentage' ? '% Off' : `Amount (${data.currency})`}
              </Label>
              <Input
                id="value"
                type="number"
                min={0}
                max={data.type === 'percentage' ? 100 : undefined}
                step={data.type === 'percentage' ? 1 : 0.01}
                value={data.value || ''}
                onChange={(e) => setData((d) => ({ ...d, value: Number(e.target.value) }))}
              />
            </div>
          )}
        </div>

        {/* Currency (fixed_amount only) */}
        {data.type === 'fixed_amount' && (
          <div className="grid gap-2">
            <Label>Currency</Label>
            <Select
              value={data.currency}
              onValueChange={(v) => setData((d) => ({ ...d, currency: v }))}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Min Order + Max Discount */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="minOrder">Min Order Amount (optional)</Label>
            <Input
              id="minOrder"
              type="number"
              min={0}
              step={0.01}
              value={data.minOrderAmount}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  minOrderAmount: e.target.value === '' ? '' : Number(e.target.value),
                }))
              }
              placeholder="0 = no minimum"
            />
          </div>
          {data.type === 'percentage' && (
            <div className="grid gap-2">
              <Label htmlFor="maxDiscount">Max Discount Amount (optional)</Label>
              <Input
                id="maxDiscount"
                type="number"
                min={0}
                step={0.01}
                value={data.maxDiscountAmount}
                onChange={(e) =>
                  setData((d) => ({
                    ...d,
                    maxDiscountAmount: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                placeholder="Cap the discount"
              />
            </div>
          )}
        </div>

        {/* Limits */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="maxRedemptions">Max Redemptions (0 = unlimited)</Label>
            <Input
              id="maxRedemptions"
              type="number"
              min={0}
              value={data.maxRedemptions}
              onChange={(e) => setData((d) => ({ ...d, maxRedemptions: Number(e.target.value) }))}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="perCustomer">Per Customer Limit (0 = unlimited)</Label>
            <Input
              id="perCustomer"
              type="number"
              min={0}
              value={data.perCustomerLimit}
              onChange={(e) => setData((d) => ({ ...d, perCustomerLimit: Number(e.target.value) }))}
            />
          </div>
        </div>

        {/* First order only */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="firstOrderOnly"
            checked={data.firstOrderOnly}
            onCheckedChange={(checked) =>
              setData((d) => ({ ...d, firstOrderOnly: checked === true }))
            }
          />
          <Label htmlFor="firstOrderOnly" className="cursor-pointer">
            First order only
          </Label>
        </div>

        {/* Validity period */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="validFrom">Valid From</Label>
            <Input
              id="validFrom"
              type="datetime-local"
              value={data.validFrom}
              onChange={(e) => setData((d) => ({ ...d, validFrom: e.target.value }))}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="validUntil">Valid Until</Label>
            <Input
              id="validUntil"
              type="datetime-local"
              value={data.validUntil}
              onChange={(e) => setData((d) => ({ ...d, validUntil: e.target.value }))}
            />
          </div>
        </div>

        {/* Targeting — multi-select pickers driven by the project's own
            catalog. Product pickers use the lite `/products/picker` endpoint
            (cursor-paginated infinite scroll + server search + on-mount chip
            hydration). Category picker stays static — categories rarely
            exceed a few dozen. */}
        <MultiSelectPicker
          id="applicableProductIds"
          label="Applicable Products"
          helperText="Leave empty to apply to all products. Search by product name or SKU."
          placeholder="All products eligible"
          asyncSource={productPickerSource}
          value={data.applicableProductIds}
          onChange={(next) => setData((d) => ({ ...d, applicableProductIds: next }))}
          emptyMessage="No products match"
        />
        <MultiSelectPicker
          id="applicableCategoryIds"
          label="Applicable Categories"
          helperText="Leave empty to apply to all categories."
          placeholder="All categories eligible"
          items={categoryPickerItems}
          value={data.applicableCategoryIds}
          onChange={(next) => setData((d) => ({ ...d, applicableCategoryIds: next }))}
          loading={loadingCategories}
          emptyMessage="No categories match"
        />
        <MultiSelectPicker
          id="excludedProductIds"
          label="Excluded Products"
          helperText="Selected products are NOT eligible for this coupon."
          placeholder="No exclusions"
          asyncSource={productPickerSource}
          value={data.excludedProductIds}
          onChange={(next) => setData((d) => ({ ...d, excludedProductIds: next }))}
          emptyMessage="No products match"
        />

        {/* Description */}
        <div className="grid gap-2">
          <Label htmlFor="description">Description (optional, internal)</Label>
          <Textarea
            id="description"
            value={data.description}
            onChange={(e) => setData((d) => ({ ...d, description: e.target.value }))}
            placeholder="Internal notes about this coupon"
            rows={2}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/projects/${projectId}/ecommerce`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Ticket className="h-6 w-6" />
              Coupons
            </h1>
            <p className="text-muted-foreground text-sm">
              {meta.total > 0
                ? `${meta.total} coupon${meta.total !== 1 ? 's' : ''} total`
                : 'Create and manage discount coupons'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setBulkFormData({ ...emptyFormData, currency: storeCurrency });
              setGeneratedCodes([]);
              setShowBulkGenerate(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Bulk Generate
          </Button>
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Create Coupon
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {report && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Coupons</CardTitle>
              <Ticket className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.totalCoupons}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Coupons</CardTitle>
              <Percent className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.activeCoupons}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Redemptions</CardTitle>
              <BarChart3 className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.totalRedemptions}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Discount Given</CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatMoney(report.totalDiscountGiven || 0, report.currency || storeCurrency, {
                  alwaysDecimals: true,
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <div className="relative">
                <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Search by coupon code..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                  <SelectItem value="free_shipping">Free Shipping</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} variant="secondary">
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coupons Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
                <p className="text-muted-foreground text-sm">Loading coupons...</p>
              </div>
            </div>
          ) : isError ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <Ticket className="text-destructive h-8 w-8" />
                <p className="text-destructive text-sm">Failed to load coupons</p>
              </div>
            </div>
          ) : coupons.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-2">
                <Ticket className="text-muted-foreground h-8 w-8" />
                <p className="text-muted-foreground text-sm">No coupons found</p>
                {(search || statusFilter !== 'all' || typeFilter !== 'all') && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setSearchInput('');
                      setStatusFilter('all');
                      setTypeFilter('all');
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="text-center">Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Valid Period</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((coupon) => (
                  <TableRow key={coupon._id}>
                    <TableCell className="font-mono font-medium">{coupon.code}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {coupon.type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{formatCouponValue(coupon)}</TableCell>
                    <TableCell className="text-center">
                      {coupon.redemptionCount}
                      {coupon.maxRedemptions > 0 ? ` / ${coupon.maxRedemptions}` : ' / ∞'}
                    </TableCell>
                    <TableCell>{getStatusBadge(coupon)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {coupon.validFrom && coupon.validUntil ? (
                        <>
                          {formatDate(coupon.validFrom)} — {formatDate(coupon.validUntil)}
                        </>
                      ) : coupon.validFrom ? (
                        <>From {formatDate(coupon.validFrom)}</>
                      ) : coupon.validUntil ? (
                        <>Until {formatDate(coupon.validUntil)}</>
                      ) : (
                        <span className="text-muted-foreground">No expiry</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditForm(coupon)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => cloneMutation.mutate(coupon._id)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {coupon.isActive ? (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setShowDeleteConfirm(coupon._id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() =>
                                updateMutation.mutate({
                                  couponId: coupon._id,
                                  data: { isActive: true },
                                })
                              }
                              disabled={updateMutation.isPending}
                            >
                              <Power className="mr-2 h-4 w-4" />
                              Activate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Showing {(meta.page - 1) * meta.limit + 1} to{' '}
            {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} coupons
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page <= 1}
              onClick={() => setPage(meta.page - 1)}
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(meta.totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (meta.totalPages <= 5) {
                pageNum = i + 1;
              } else if (meta.page <= 3) {
                pageNum = i + 1;
              } else if (meta.page >= meta.totalPages - 2) {
                pageNum = meta.totalPages - 4 + i;
              } else {
                pageNum = meta.page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === meta.page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page >= meta.totalPages}
              onClick={() => setPage(meta.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
            <DialogDescription>
              {editingCoupon
                ? 'Update the coupon details below.'
                : 'Fill in the details to create a new discount coupon.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {renderCouponForm(formData, setFormData)}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={closeForm}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createMutation.isPending || updateMutation.isPending || !formData.code.trim()
                }
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingCoupon ? 'Save Changes' : 'Create Coupon'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={Boolean(showDeleteConfirm)} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Coupon?</DialogTitle>
            <DialogDescription>
              This will deactivate the coupon so it can no longer be redeemed. This action can be
              reversed by editing the coupon.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => showDeleteConfirm && deleteMutation.mutate(showDeleteConfirm)}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Generate Dialog */}
      <Dialog
        open={showBulkGenerate}
        onOpenChange={(open) => {
          if (!open) {
            setShowBulkGenerate(false);
            setGeneratedCodes([]);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Generate Coupons</DialogTitle>
            <DialogDescription>
              Generate multiple unique coupon codes with shared discount rules.
            </DialogDescription>
          </DialogHeader>
          {generatedCodes.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{generatedCodes.length} codes generated</p>
                <Button variant="outline" size="sm" onClick={downloadGeneratedCodes}>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
              <div className="bg-muted/30 max-h-60 overflow-y-auto rounded-md border p-3">
                <div className="grid gap-1 font-mono text-sm">
                  {generatedCodes.map((code) => (
                    <div key={code} className="hover:bg-muted rounded px-2 py-0.5">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowBulkGenerate(false);
                    setGeneratedCodes([]);
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleBulkSubmit}>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="bulkCount">Number of Codes</Label>
                    <Input
                      id="bulkCount"
                      type="number"
                      min={1}
                      max={1000}
                      value={bulkCount}
                      onChange={(e) => setBulkCount(Number(e.target.value))}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="bulkPrefix">Prefix (optional)</Label>
                    <Input
                      id="bulkPrefix"
                      value={bulkPrefix}
                      onChange={(e) => setBulkPrefix(e.target.value.toUpperCase())}
                      placeholder="e.g. SUMMER"
                      className="uppercase"
                    />
                  </div>
                </div>
                {renderCouponForm(bulkFormData, setBulkFormData)}
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setShowBulkGenerate(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={bulkGenerateMutation.isPending}>
                  {bulkGenerateMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Generate {bulkCount} Codes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
